import {
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
} from "react";

import {
  FaPlay,
  FaPause,
  FaRedo,
  FaExpand,
  FaForward,
  FaBackward,
  FaCog,
} from "react-icons/fa";

import useAnimationEngine from "../../hooks/useAnimationEngine.js";
import useAudioEngine from "../../hooks/useAudioEngine.js";
import "../../styles/visualization/visualizationStage.css";
import { formatTime } from "../../utils/formatTime.js";

// ─── Canvas drawing helpers (identical to CanvasEditorDisplay) ────────────────

const WORLD_WIDTH = 1600;
const WORLD_HEIGHT = 900;

const drawRoundedRect = (
  ctx,
  x,
  y,
  w,
  h,
  { tl = 0, tr = 0, br = 0, bl = 0 } = {},
) => {
  const rtlRaw = Math.max(0, tl);
  const rtrRaw = Math.max(0, tr);
  const rbrRaw = Math.max(0, br);
  const rblRaw = Math.max(0, bl);

  const factor = Math.min(
    1,
    w / Math.max(0.0001, rtlRaw + rtrRaw),
    w / Math.max(0.0001, rblRaw + rbrRaw),
    h / Math.max(0.0001, rtlRaw + rblRaw),
    h / Math.max(0.0001, rtrRaw + rbrRaw),
  );

  const rtl = rtlRaw * factor;
  const rtr = rtrRaw * factor;
  const rbr = rbrRaw * factor;
  const rbl = rblRaw * factor;

  ctx.beginPath();
  ctx.moveTo(x + rtl, y);
  ctx.lineTo(x + w - rtr, y);
  ctx.arcTo(x + w, y, x + w, y + rtr, rtr);
  ctx.lineTo(x + w, y + h - rbr);
  ctx.arcTo(x + w, y + h, x + w - rbr, y + h, rbr);
  ctx.lineTo(x + rbl, y + h);
  ctx.arcTo(x, y + h, x, y + h - rbl, rbl);
  ctx.lineTo(x, y + rtl);
  ctx.arcTo(x, y, x + rtl, y, rtl);
  ctx.closePath();
};

const buildShapePath = (ctx, rect, component, cameraZoom, scale) => {
  const shape = component.shape || "rect";
  if (shape === "circle" || shape === "ellipse") {
    ctx.beginPath();
    ctx.ellipse(
      rect.x + rect.w / 2,
      rect.y + rect.h / 2,
      Math.max(0.0001, rect.w / 2),
      Math.max(0.0001, rect.h / 2),
      0,
      0,
      Math.PI * 2,
    );
    ctx.closePath();
    return;
  }

  if (shape === "diamond") {
    ctx.beginPath();
    ctx.moveTo(rect.x + rect.w / 2, rect.y);
    ctx.lineTo(rect.x + rect.w, rect.y + rect.h / 2);
    ctx.lineTo(rect.x + rect.w / 2, rect.y + rect.h);
    ctx.lineTo(rect.x, rect.y + rect.h / 2);
    ctx.closePath();
    return;
  }

  if (shape === "triangle") {
    ctx.beginPath();
    ctx.moveTo(rect.x + rect.w / 2, rect.y);
    ctx.lineTo(rect.x + rect.w, rect.y + rect.h);
    ctx.lineTo(rect.x, rect.y + rect.h);
    ctx.closePath();
    return;
  }

  drawRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, {
    tl: Number(component.radiusTopLeft || 0) * cameraZoom * scale,
    tr: Number(component.radiusTopRight || 0) * cameraZoom * scale,
    br: Number(component.radiusBottomRight || 0) * cameraZoom * scale,
    bl: Number(component.radiusBottomLeft || 0) * cameraZoom * scale,
  });
};

const applyLineStyle = (ctx, type) => {
  if (type === "dashed") ctx.setLineDash([6, 4]);
  else if (type === "dotted") ctx.setLineDash([1, 3]);
  else ctx.setLineDash([]);
};

const getBorderPoint = (from, to) => {
  const dx = to.cx - from.cx;
  const dy = to.cy - from.cy;
  const hw = from.width / 2;
  const hh = from.height / 2;
  const s =
    Math.abs(dy) * hw > Math.abs(dx) * hh
      ? hh / Math.max(0.0001, Math.abs(dy))
      : hw / Math.max(0.0001, Math.abs(dx));
  return { x: from.cx + dx * s, y: from.cy + dy * s };
};

const breakLongWord = (ctx, word, maxWidth) => {
  const parts = [];
  let current = "";
  for (const ch of word) {
    const next = current + ch;
    if (current && ctx.measureText(next).width > maxWidth) {
      parts.push(current);
      current = ch;
    } else {
      current = next;
    }
  }
  if (current) parts.push(current);
  return parts;
};

const wrapTextLines = (ctx, text, maxWidth) => {
  const safeWidth = Math.max(1, maxWidth);
  const paragraphs = String(text ?? "").split("\n");
  const lines = [];

  paragraphs.forEach((p, pIndex) => {
    if (!p.length) {
      lines.push("");
      lines.push("");
      return;
    }
    const tokens = p.split(/(\s+)/);
    let line = "";

    tokens.forEach((token) => {
      const candidate = line ? line + token : token;
      if (ctx.measureText(candidate).width <= safeWidth) {
        line = candidate;
        return;
      }
      if (line) {
        lines.push(line);
        line = "";
      }
      if (ctx.measureText(token).width <= safeWidth) {
        line = token;
      } else {
        const chunks = breakLongWord(ctx, token, safeWidth);
        chunks.forEach((chunk, idx) => {
          if (idx < chunks.length - 1) {
            lines.push(chunk);
          } else {
            line = chunk;
          }
        });
      }
    });

    if (line) lines.push(line);
    if (pIndex < paragraphs.length - 1) lines.push("");
  });

  return lines.length ? lines : [""];
};

const drawTextLine = (ctx, text, x, y, letterSpacing = 0) => {
  const spacing = Number(letterSpacing || 0);
  if (Math.abs(spacing) < 0.0001) {
    ctx.fillText(text, x, y);
    return;
  }

  const content = String(text ?? "");
  const chars = [...content];
  if (!chars.length) return;

  let currentX = x;
  if (ctx.textAlign === "center") {
    const totalWidth =
      ctx.measureText(content).width + Math.max(0, chars.length - 1) * spacing;
    currentX -= totalWidth / 2;
    ctx.textAlign = "left";
  } else if (ctx.textAlign === "right") {
    const totalWidth =
      ctx.measureText(content).width + Math.max(0, chars.length - 1) * spacing;
    currentX -= totalWidth;
    ctx.textAlign = "left";
  }

  chars.forEach((char, index) => {
    ctx.fillText(char, currentX, y);
    currentX += ctx.measureText(char).width;
    if (index < chars.length - 1) currentX += spacing;
  });
};

const applyShadowStyle = (ctx, component, cameraZoom, scale) => {
  ctx.shadowColor = component.shadowColor || "#00000000";
  ctx.shadowBlur = Number(component.shadowBlur || 0) * cameraZoom * scale;
  ctx.shadowOffsetX = Number(component.shadowOffsetX || 0) * cameraZoom * scale;
  ctx.shadowOffsetY = -Number(component.shadowOffsetY || 0) * cameraZoom * scale;
};

const clearShadowStyle = (ctx) => {
  ctx.shadowColor = "#00000000";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// ─── Component ────────────────────────────────────────────────────────────────

const VisualizationStage = ({ animation }) => {
  const fullscreenRef = useRef(null);
  const stageInnerRef = useRef(null);
  const canvasRef = useRef(null);
  const hideTimerRef = useRef(null);
  const settingsRef = useRef(null);

  const [layoutTick, setLayoutTick] = useState(0);
  const [stageHeight, setStageHeight] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [speed, setSpeedState] = useState(1);
  const [scrubberHover, setScrubberHover] = useState(null);

  // ── outside-click closes settings ──
  useEffect(() => {
    if (!showSettings) return;
    const handleOutsideClick = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target))
        setShowSettings(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showSettings]);

  if (
    !animation ||
    !animation.animationComponents ||
    !animation.timelines ||
    !animation.connectors
  )
    return null;

  // ── data ──
  const initialComponents = useMemo(
    () =>
      animation.animationComponents.map((ac) => ({
        ...ac.component,
        animationComponentId: ac.animationComponentId,
        animationComponentName: ac.animationComponentName,
        shape: ac.component?.shape || "rect",
      })),
    [animation.animationId],
  );

  const timelines = useMemo(
    () => animation.timelines || [],
    [animation.animationId],
  );
  const connectors = useMemo(
    () => animation.connectors || [],
    [animation.animationId],
  );
  const camera = useMemo(() => animation.camera ?? {}, [animation.animationId]);
  const cameraTimelines = useMemo(
    () => animation.cameraTimelines ?? [],
    [animation.animationId],
  );
  const chapters = useMemo(() => {
    const source = Array.isArray(animation.chapters) ? animation.chapters : [];
    return [...source].sort(
      (a, b) => Number(a?.time || 0) - Number(b?.time || 0),
    );
  }, [animation.animationId, animation.chapters]);
  const audioTracks = useMemo(
    () => animation.audioTracks ?? [],
    [animation.animationId, animation.audioTracks],
  );
  const duration = animation.metadata?.duration || 0;

  const {
    components,
    camera: animatedCamera,
    play,
    pause,
    reset,
    seek,
    isPlaying,
    currentTime,
    setSpeed,
  } = useAnimationEngine(
    initialComponents,
    timelines,
    camera,
    cameraTimelines,
    duration,
  );

  const audioEngine = useAudioEngine({
    audioTracks,
    isPlaying,
    currentTime,
    duration,
  });

  // ── helpers ──
  const togglePlayPause = () => {
    if (isPlaying) pause();
    else play();
  };

  const jumpTime = (delta) => {
    const t = clamp(currentTime + delta, 0, duration);
    seek(t);
    audioEngine.seekAudio(t);
  };

  const getCurrentChapter = useCallback(
    (time) => {
      if (!chapters.length) return null;
      let current = null;
      for (const c of chapters) {
        if (time >= c.time) current = c;
        else break;
      }
      return current;
    },
    [chapters],
  );

  const handleScrubberMouseMove = useCallback(
    (e) => {
      if (!duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width),
      );
      const hoverTime = ratio * duration;
      const chapter = getCurrentChapter(hoverTime);
      setScrubberHover({
        xPct: ratio * 100,
        label: chapter ? chapter.title : formatTime(hoverTime),
      });
    },
    [duration, getCurrentChapter],
  );

  const handleScrubberMouseLeave = useCallback(
    () => setScrubberHover(null),
    [],
  );

  const handleSetSpeed = (s) => {
    setSpeedState(s);
    setSpeed(s);
    setShowSettings(false);
  };

  const toggleFullscreen = () => {
    const el = fullscreenRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  // ── fullscreen auto-hide ──
  const startHideTimer = () => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setShowControls(false), 2000);
  };

  const revealControls = () => {
    if (!document.fullscreenElement) return;
    setShowControls(true);
    startHideTimer();
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      if (document.fullscreenElement) {
        setShowControls(true);
        startHideTimer();
      } else {
        setShowControls(true);
        clearTimeout(hideTimerRef.current);
      }
      setLayoutTick((n) => n + 1);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  // ── stage size ──
  useLayoutEffect(() => {
    if (!stageInnerRef.current) return;
    const r = stageInnerRef.current.getBoundingClientRect();
    setStageHeight(r.height);
  }, [layoutTick]);

  useEffect(() => {
    const onResize = () => setLayoutTick((n) => n + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── keyboard ──
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return;
      switch (e.code) {
        case "Space":
          e.preventDefault();
          togglePlayPause();
          break;
        case "ArrowRight":
          jumpTime(5);
          break;
        case "ArrowLeft":
          jumpTime(-5);
          break;
        case "KeyF":
          toggleFullscreen();
          break;
        default:
          break;
      }
      revealControls();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [currentTime, duration, isPlaying]);

  // ── visible connectors ──
  const visibleConnectors = useMemo(
    () =>
      connectors
        .filter((c) => currentTime >= c.startTime && currentTime <= c.endTime)
        .sort((a, b) => (a.z ?? 0) - (b.z ?? 0)),
    [connectors, currentTime],
  );

  const currentChapter = getCurrentChapter(currentTime);

  // ── canvas draw ──
  useEffect(() => {
    const canvas = canvasRef.current;
    const host = stageInnerRef.current;
    if (!canvas || !host) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = host.getBoundingClientRect();
    const width = Math.max(1, rect.width);
    const height = Math.max(1, rect.height);

    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const scale = Math.min(width / WORLD_WIDTH, height / WORLD_HEIGHT);
    const offsetX = (width - WORLD_WIDTH * scale) / 2;
    const offsetY = (height - WORLD_HEIGHT * scale) / 2;

    const stageCenterX = 800;
    const stageCenterY = 450;
    const cameraZoom = Math.max(Number(animatedCamera?.zoom ?? 1), 0.0001);
    const cameraRotation = Number(animatedCamera?.rotation || 0);
    const cameraWorldX = Number(animatedCamera?.x ?? 800);
    const cameraWorldY = Number(animatedCamera?.y ?? 450);

    const projectComponent = (c) => {
      const wx = Number(c.x || 0);
      const wy = Number(c.y || 0);
      const ch = Number(c.height || 0);
      const cw = Number(c.width || 0);
      const ox = Number(c.originX || 0);
      const oy = Number(c.originY || 0);

      const dx = wx + ox - cameraWorldX;
      const dy = wy + oy - cameraWorldY;

      const screenOriginX = stageCenterX + dx * cameraZoom;
      const screenOriginY = WORLD_HEIGHT - (stageCenterY + dy * cameraZoom);

      return {
        x: offsetX + (screenOriginX - ox * cameraZoom) * scale,
        y: offsetY + (screenOriginY - (ch - oy) * cameraZoom) * scale,
        w: cw * cameraZoom * scale,
        h: ch * cameraZoom * scale,
        ox: ox * cameraZoom * scale,
        oy: (ch - oy) * cameraZoom * scale,
      };
    };

      const projectedById = new Map();
      components.forEach((c) => {
        projectedById.set(c.animationComponentId, projectComponent(c));
      });

    // camera rotation context
    ctx.save();
    ctx.translate(
      offsetX + stageCenterX * scale,
      offsetY + stageCenterY * scale,
    );
    ctx.rotate((-cameraRotation * Math.PI) / 180);
    ctx.translate(
      -(offsetX + stageCenterX * scale),
      -(offsetY + stageCenterY * scale),
    );

    // ── connectors ──
    visibleConnectors.forEach((conn) => {
      const from = projectedById.get(conn.from.animationComponentId);
      const to = projectedById.get(conn.to.animationComponentId);
      if (!from || !to) return;

      const fromRect = {
        cx: from.x + from.w / 2,
        cy: from.y + from.h / 2,
        width: from.w,
        height: from.h,
      };
      const toRect = {
        cx: to.x + to.w / 2,
        cy: to.y + to.h / 2,
        width: to.w,
        height: to.h,
      };

      const start = getBorderPoint(fromRect, toRect);
      const end = getBorderPoint(toRect, fromRect);

      const thickness = Number(conn.thickness || 2) * scale;
      const arrow = thickness * 2.8;
      const headBaseOff = arrow * Math.cos(Math.PI / 6);
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const len = Math.hypot(dx, dy);
      const shaftEnd =
        len > headBaseOff
          ? {
              x: end.x - (dx / len) * headBaseOff,
              y: end.y - (dy / len) * headBaseOff,
            }
          : end;

      ctx.strokeStyle = conn.color || "#ffffff";
      ctx.lineWidth = thickness;
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(shaftEnd.x, shaftEnd.y);
      ctx.stroke();

      const angle = Math.atan2(dy, dx);
      ctx.fillStyle = conn.color || "#ffffff";
      ctx.beginPath();
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(
        end.x - arrow * Math.cos(angle - Math.PI / 6),
        end.y - arrow * Math.sin(angle - Math.PI / 6),
      );
      ctx.lineTo(
        end.x - arrow * Math.cos(angle + Math.PI / 6),
        end.y - arrow * Math.sin(angle + Math.PI / 6),
      );
      ctx.closePath();
      ctx.fill();
    });

    // ── components ──
      const sorted = [...components].sort((a, b) => {
        if ((a.z || 0) !== (b.z || 0)) return (a.z || 0) - (b.z || 0);
        return 0;
      });
      sorted.forEach((c) => {
        if (!c.visibility) return;
        const p = projectedById.get(c.animationComponentId);
        if (!p) return;

        const isAxisAligned =
          Math.abs(Number(c.rotate || 0)) < 0.0001 &&
          Math.abs(cameraRotation) < 0.0001 &&
          (c.shape || "rect") === "rect";

        ctx.save();
        ctx.translate(p.x + p.ox, p.y + p.oy);
        ctx.rotate((Number(c.rotate || 0) * Math.PI) / 180);
        ctx.translate(-(p.x + p.ox), -(p.y + p.oy));
        ctx.globalAlpha = Math.max(0, Math.min(1, Number(c.opacity ?? 1)));

        buildShapePath(ctx, p, c, cameraZoom, scale);
        applyShadowStyle(ctx, c, cameraZoom, scale);
        ctx.fillStyle = c.backgroundColor || "#000000";
        ctx.fill();

      // borders
      const topW = Number(c.borderTopWidth || 0) * cameraZoom * scale;
      const rightW = Number(c.borderRightWidth || 0) * cameraZoom * scale;
      const bottomW = Number(c.borderBottomWidth || 0) * cameraZoom * scale;
      const leftW = Number(c.borderLeftWidth || 0) * cameraZoom * scale;
      const hasBorder = topW > 0 || rightW > 0 || bottomW > 0 || leftW > 0;
      const topType = c.borderTopType || "solid";
      const rightType = c.borderRightType || "solid";
      const bottomType = c.borderBottomType || "solid";
      const leftType = c.borderLeftType || "solid";

        const uniformSolid =
          hasBorder &&
          (c.shape || "rect") === "rect" &&
          Math.abs(topW - rightW) < 0.0001 &&
        Math.abs(topW - bottomW) < 0.0001 &&
        Math.abs(topW - leftW) < 0.0001 &&
        (c.borderTopColor || "#000000") === (c.borderRightColor || "#000000") &&
        (c.borderTopColor || "#000000") ===
          (c.borderBottomColor || "#000000") &&
        (c.borderTopColor || "#000000") === (c.borderLeftColor || "#000000") &&
        topType === "solid" &&
        rightType === "solid" &&
        bottomType === "solid" &&
        leftType === "solid";

        if (uniformSolid) {
          const bw = topW;
          ctx.save();
          drawRoundedRect(ctx, p.x - bw / 2, p.y - bw / 2, p.w + bw, p.h + bw, {
            tl: Number(c.radiusTopLeft || 0) * cameraZoom * scale + bw / 2,
            tr: Number(c.radiusTopRight || 0) * cameraZoom * scale + bw / 2,
            br: Number(c.radiusBottomRight || 0) * cameraZoom * scale + bw / 2,
            bl: Number(c.radiusBottomLeft || 0) * cameraZoom * scale + bw / 2,
          });
          ctx.lineWidth = bw;
          ctx.strokeStyle = c.borderTopColor || "#000000";
          ctx.setLineDash([]);
          ctx.stroke();
          ctx.restore();
        } else if ((c.shape || "rect") === "rect") {
          if (topW > 0) {
            if (isAxisAligned && topType === "solid") {
              ctx.fillStyle = c.borderTopColor || "#000000";
              ctx.fillRect(p.x - leftW, p.y - topW, p.w + leftW + rightW, topW);
            } else {
              ctx.save();
              ctx.lineWidth = topW;
              ctx.strokeStyle = c.borderTopColor || "#000000";
              ctx.lineCap = "butt";
              applyLineStyle(ctx, topType);
              ctx.beginPath();
              ctx.moveTo(p.x, p.y + topW / 2);
              ctx.lineTo(p.x + p.w, p.y + topW / 2);
              ctx.stroke();
              ctx.restore();
            }
          }

          if (rightW > 0) {
            if (isAxisAligned && rightType === "solid") {
              ctx.fillStyle = c.borderRightColor || "#000000";
              ctx.fillRect(p.x + p.w, p.y - topW, rightW, p.h + topW + bottomW);
            } else {
              ctx.save();
              ctx.lineWidth = rightW;
              ctx.strokeStyle = c.borderRightColor || "#000000";
              ctx.lineCap = "butt";
              applyLineStyle(ctx, rightType);
              const x = p.x + p.w - rightW / 2;
              ctx.beginPath();
              ctx.moveTo(x, p.y);
              ctx.lineTo(x, p.y + p.h);
              ctx.stroke();
              ctx.restore();
            }
          }

          if (bottomW > 0) {
            if (isAxisAligned && bottomType === "solid") {
              ctx.fillStyle = c.borderBottomColor || "#000000";
              ctx.fillRect(p.x - leftW, p.y + p.h, p.w + leftW + rightW, bottomW);
            } else {
              ctx.save();
              ctx.lineWidth = bottomW;
              ctx.strokeStyle = c.borderBottomColor || "#000000";
              ctx.lineCap = "butt";
              applyLineStyle(ctx, bottomType);
              const y = p.y + p.h - bottomW / 2;
              ctx.beginPath();
              ctx.moveTo(p.x, y);
              ctx.lineTo(p.x + p.w, y);
              ctx.stroke();
              ctx.restore();
            }
          }

          if (leftW > 0) {
            if (isAxisAligned && leftType === "solid") {
              ctx.fillStyle = c.borderLeftColor || "#000000";
              ctx.fillRect(p.x - leftW, p.y - topW, leftW, p.h + topW + bottomW);
            } else {
              ctx.save();
              ctx.lineWidth = leftW;
              ctx.strokeStyle = c.borderLeftColor || "#000000";
              ctx.lineCap = "butt";
              applyLineStyle(ctx, leftType);
              const x = p.x + leftW / 2;
              ctx.beginPath();
              ctx.moveTo(x, p.y);
              ctx.lineTo(x, p.y + p.h);
              ctx.stroke();
              ctx.restore();
            }
          }
        } else if (hasBorder) {
          ctx.save();
          buildShapePath(ctx, p, c, cameraZoom, scale);
          ctx.lineWidth = Math.max(topW, rightW, bottomW, leftW);
          ctx.strokeStyle = c.borderTopColor || "#000000";
          ctx.setLineDash([]);
          ctx.stroke();
          ctx.restore();
        }

        clearShadowStyle(ctx);

        // text
        if (c.text) {
          if (c.overflow === "hidden") {
            buildShapePath(ctx, p, c, cameraZoom, scale);
            ctx.clip();
          }
          ctx.fillStyle = c.color || "#ffffff";
        const fontSize = Number(c.fontSize || 16) * cameraZoom * scale;
        const fontWeight = Number(c.fontWeight || 400);
        const fontFamily = "JetBrains Mono, Consolas, Monaco, monospace";
        ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
        const textPad = 8 * scale;
        const textMaxW = Math.max(1, p.w - textPad * 2);
        const lineHeight = fontSize * Math.max(Number(c.lineHeight || 1.2), 0.1);
        const letterSpacing = Number(c.letterSpacing || 0) * cameraZoom * scale;
        const lines = wrapTextLines(ctx, c.text, textMaxW);
        const sample = ctx.measureText("Mg");
        const ascent = sample.actualBoundingBoxAscent || fontSize * 0.8;
        const descent = sample.actualBoundingBoxDescent || fontSize * 0.2;
        const baseOff = (lineHeight - (ascent + descent)) / 2 + ascent;
        const totalH = lines.length * lineHeight;
        const firstY =
          c.verticalAlign === "top"
            ? p.y + textPad + baseOff
            : c.verticalAlign === "bottom"
              ? p.y + p.h - textPad - totalH + baseOff
              : p.y + (p.h - totalH) / 2 + baseOff;

        ctx.textBaseline = "alphabetic";
        ctx.textAlign =
          c.textAlign === "left"
            ? "left"
            : c.textAlign === "right"
              ? "right"
              : "center";
        const textX =
          c.textAlign === "left"
            ? p.x + textPad
            : c.textAlign === "right"
              ? p.x + p.w - textPad
              : p.x + p.w / 2;
        lines.forEach((line, idx) =>
          drawTextLine(ctx, line, textX, firstY + idx * lineHeight, letterSpacing),
        );
      }

      ctx.restore();
    });

    ctx.restore(); // camera rotation
  }, [components, animatedCamera, visibleConnectors, currentTime, layoutTick]);

  // ── render ──
  return (
    <section
      className="viz-stage-wrapper"
      ref={fullscreenRef}
      onMouseMove={revealControls}
      onTouchStart={revealControls}
    >
      <div
        className={`viz-stage-controls ${showControls ? "visible" : "hidden"}`}
      >
        <button onClick={() => jumpTime(-5)}>
          <FaBackward />
        </button>
        <button onClick={togglePlayPause}>
          {isPlaying ? <FaPause /> : <FaPlay />}
        </button>
        <button onClick={() => jumpTime(5)}>
          <FaForward />
        </button>
        <button
          onClick={() => {
            reset();
            audioEngine.stopAll();
            setLayoutTick((n) => n + 1);
          }}
        >
          <FaRedo />
        </button>
        <button onClick={toggleFullscreen}>
          <FaExpand />
        </button>

        {duration > 0 && (
          <div
            className="viz-scrubber-shell"
            onMouseMove={handleScrubberMouseMove}
            onMouseLeave={handleScrubberMouseLeave}
          >
            <input
              type="range"
              className="viz-stage-scrubber"
              min={0}
              max={duration}
              step={0.01}
              value={currentTime}
              onChange={(e) => {
                const t = Number(e.target.value);
                seek(t);
                audioEngine.seekAudio(t);
              }}
              disabled={isPlaying}
            />
            {scrubberHover && (
              <div
                className="viz-scrubber-tooltip"
                style={{ left: `${scrubberHover.xPct}%` }}
              >
                {scrubberHover.label}
              </div>
            )}
          </div>
        )}

        <span className="viz-stage-time">
          {currentChapter && (
            <span className="viz-stage-chapter-label">
              {currentChapter.title} ·{" "}
            </span>
          )}
          <span className="viz-stage-time-value">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </span>

        <div className="viz-settings-wrapper" ref={settingsRef}>
          <button
            className={`viz-settings-btn ${showSettings ? "active" : ""}`}
            onClick={() => setShowSettings((v) => !v)}
            title="Settings"
          >
            <FaCog />
          </button>

          {showSettings && (
            <div
              className="viz-settings-panel"
              style={{
                maxHeight: stageHeight
                  ? `${Math.max(Math.floor(stageHeight - 8), 180)}px`
                  : "70vh",
              }}
            >
              <div className="viz-settings-section-label">Playback Speed</div>
              <div className="viz-settings-speed-options">
                {[0.25, 0.5, 1, 2, 4].map((s) => (
                  <button
                    key={s}
                    className={`viz-settings-speed-btn ${speed === s ? "active" : ""}`}
                    onClick={() => handleSetSpeed(s)}
                  >
                    {s}x
                  </button>
                ))}
              </div>

              {chapters.length > 0 && (
                <>
                  <div className="viz-settings-section-label">Chapters</div>
                  <div className="viz-settings-chapter-list">
                    {chapters.map((chapter, index) => {
                      const chapterTime = Number(chapter?.time || 0);
                      const chapterTitle = String(chapter?.title || "Untitled");
                      return (
                        <button
                          key={`${chapter.title}-${chapter.time}-${index}-list`}
                          className={`viz-settings-chapter-btn ${currentChapter?.title === chapter.title ? "active" : ""}`}
                          onClick={() => {
                            seek(chapterTime);
                            audioEngine.seekAudio(chapterTime);
                            setShowSettings(false);
                          }}
                        >
                          <span className="viz-settings-chapter-time">
                            {formatTime(chapterTime)}
                          </span>
                          <span className="viz-settings-chapter-title">
                            {chapterTitle}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Canvas replaces the old div-based stage */}
      <div
        ref={stageInnerRef}
        className="viz-stage"
        onClick={togglePlayPause}
        style={{ position: "relative", overflow: "hidden" }}
      >
        <canvas ref={canvasRef} style={{ display: "block" }} />
      </div>
    </section>
  );
};

export default VisualizationStage;
